import type { RealtimeChannel } from '@supabase/supabase-js';
import { supabase } from '@/lib/supabase';

/**
 * One Realtime channel per topic, shared by everything on the page that listens to it.
 *
 * Triggers broadcast small notices on private channels (`sheet:<document id>`,
 * `quotes:<org id>`; see 20261030130000_realtime_broadcast.sql), and Realtime lets a
 * session join only when the policies on realtime.messages say it may read the
 * document. A notice carries ids, never cell data: listeners read what changed
 * through PostgREST, under RLS.
 *
 * Grid rows, borrowed lists, the history panel and analysis sources all listen to the
 * same document, so the channel is reference-counted: the first listener opens it,
 * the last one to leave removes it. A background browser tab gives its channels up
 * after a while; listeners hear `resync` when they come back, as after a dropped
 * connection, because whatever happened meanwhile was not sent.
 */
export type ChannelEvent = 'rows' | 'lists' | 'quotes';

export interface ChannelListener {
  readonly event: ChannelEvent;
  readonly onMessage: (payload: Record<string, unknown>) => void;
  /** Notices may have been missed (a dropped connection, a hidden tab); read again. */
  readonly onResync?: () => void;
}

interface Entry {
  channel: RealtimeChannel | null;
  readonly listeners: Set<ChannelListener>;
  /** Joined at least once: a later join is a rejoin, and missed something. */
  joined: boolean;
}

const EVENTS: readonly ChannelEvent[] = ['rows', 'lists', 'quotes'];
/** How long a hidden browser tab keeps its channels. */
export const HIDDEN_GRACE_MS = 60_000;

const entries = new Map<string, Entry>();
let hiddenTimer: ReturnType<typeof setTimeout> | null = null;
let paused = false;

function open(topic: string, entry: Entry) {
  let channel = supabase.channel(topic, { config: { private: true } });
  for (const event of EVENTS) {
    channel = channel.on('broadcast', { event }, (message) => {
      const payload = (message.payload ?? {}) as Record<string, unknown>;
      for (const listener of [...entry.listeners]) if (listener.event === event) listener.onMessage(payload);
    });
  }
  let interrupted = false;
  entry.channel = channel.subscribe((status) => {
    if (status === 'SUBSCRIBED') {
      if (entry.joined || interrupted) for (const listener of [...entry.listeners]) listener.onResync?.();
      entry.joined = true;
      interrupted = false;
    } else if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT' || status === 'CLOSED') {
      interrupted = true;
    }
  });
}

function close(entry: Entry) {
  if (!entry.channel) return;
  void supabase.removeChannel(entry.channel);
  entry.channel = null;
}

/** Starts listening to `topic`; the returned function stops. */
export function listenToTopic(topic: string, listener: ChannelListener): () => void {
  installVisibility();
  let entry = entries.get(topic);
  if (!entry) {
    entry = { channel: null, listeners: new Set(), joined: false };
    entries.set(topic, entry);
    if (!paused) open(topic, entry);
  }
  entry.listeners.add(listener);
  const owned = entry;
  return () => {
    owned.listeners.delete(listener);
    if (owned.listeners.size > 0 || entries.get(topic) !== owned) return;
    entries.delete(topic);
    close(owned);
  };
}

let visibilityInstalled = false;
function installVisibility() {
  if (visibilityInstalled || typeof document === 'undefined') return;
  visibilityInstalled = true;
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') {
      if (hiddenTimer === null && !paused) {
        hiddenTimer = setTimeout(() => {
          hiddenTimer = null;
          paused = true;
          for (const entry of entries.values()) close(entry);
        }, HIDDEN_GRACE_MS);
      }
      return;
    }
    if (hiddenTimer !== null) { clearTimeout(hiddenTimer); hiddenTimer = null; }
    if (!paused) return;
    paused = false;
    // Reopened channels report a rejoin, so every listener reads again.
    for (const [topic, entry] of entries) { entry.joined = true; open(topic, entry); }
  });
}

/** For tests: how many topics have an open channel. */
export function openTopicCount(): number {
  return [...entries.values()].filter((entry) => entry.channel !== null).length;
}
